import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { FaTimes, FaUserPlus, FaEnvelope, FaLink, FaCheck } from 'react-icons/fa';
import { ifsService } from '../../../services/ifsService';
import { vendorLinkService } from '../../../services/vendorLinkService';
import { getActiveIFSAccountId } from '../../../utils/ifsAccount';
import { ActionButton } from './IFSShippingDashboard.styles';
const row = (label, value) => value ? (_jsxs("div", { style: { display: 'flex', gap: '0.75rem', padding: '0.35rem 0', borderBottom: '1px solid var(--border-color)' }, children: [_jsx("span", { style: { width: 110, flexShrink: 0, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }, children: label }), _jsx("span", { style: { color: 'var(--text-primary)', fontSize: '0.875rem' }, children: value })] })) : null;
const AddressDetailPanel = ({ kind, summary, onClose }) => {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(null);
    // Link-to-user picker state (senders only)
    const [showLinkPicker, setShowLinkPicker] = useState(false);
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = kind === 'sender'
                    ? await ifsService.getClientAddressData(String(summary.id))
                    : await ifsService.getRecipientData(String(summary.id));
                if (!cancelled && res.ok && res.data?.success) {
                    const d = res.data.data;
                    setDetail(d.client_address_data || d.recipient_data || null);
                }
            }
            finally {
                if (!cancelled)
                    setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [kind, summary.id]);
    // Normalized fields across sender (plain names) and recipient (client_-prefixed) shapes
    const f = (plain, prefixed) => detail?.[plain] ?? detail?.[prefixed] ?? summary?.[plain] ?? '';
    const name = f('name', 'client_name') || summary.name;
    const company = f('company_name', 'client_company_name') || summary.company;
    const email = f('email', 'client_email');
    const phone = f('phone', 'client_phone');
    const address1 = f('address1', 'client_address1') || summary.address;
    const address2 = f('address2', 'client_address2');
    const city = f('city', 'client_city');
    const state = f('state', 'client_state');
    const zip = f('zip', 'client_zip');
    const country = f('country', 'client_country');
    const handleCreateSupplier = async () => {
        setBusy('supplier');
        setStatus(null);
        try {
            const res = await vendorLinkService.createSupplier({
                companyName: company || name,
                contactPerson: name,
                email: email || 'unknown@unknown.com',
                phone: phone || 'N/A',
                fax: f('fax', 'client_fax') || undefined,
                supplierCode: `IFS-${summary.id}`,
                supplierType: 'other',
                address: {
                    street: [address1, address2].filter(Boolean).join(', ') || 'N/A',
                    city: city || 'N/A',
                    state: state || 'N/A',
                    zipCode: zip || 'N/A',
                    country: country || 'United States',
                },
                notes: `Created from IFS Address Book (${kind} #${summary.id})`,
                tags: ['ifs-import'],
            });
            if (res.ok && res.data?.success) {
                setStatus({ kind: 'ok', msg: `Supplier created (code IFS-${summary.id}) — visible in Supplier Management.` });
                return res.data.supplier?.id;
            }
            setStatus({ kind: 'err', msg: res.data?.error || res.error || 'Failed to create supplier' });
        }
        catch (e) {
            setStatus({ kind: 'err', msg: e.message });
        }
        finally {
            setBusy(null);
        }
        return undefined;
    };
    const handleInvite = async () => {
        if (!email) {
            setStatus({ kind: 'err', msg: 'No email on this address — IFS has no email recorded for it.' });
            return;
        }
        setBusy('invite');
        setStatus(null);
        try {
            // Create (or reuse) the supplier record so registration auto-links it
            let supplierId;
            const sup = await vendorLinkService.createSupplier({
                companyName: company || name, contactPerson: name, email, phone: phone || 'N/A',
                supplierCode: `IFS-${summary.id}`, supplierType: 'other',
                address: { street: address1 || 'N/A', city: city || 'N/A', state: state || 'N/A', zipCode: zip || 'N/A', country: country || 'United States' },
                notes: `Created from IFS Address Book (${kind} #${summary.id})`, tags: ['ifs-import'],
            });
            if (sup.ok && sup.data?.success)
                supplierId = sup.data.supplier?.id;
            // 409 = supplier already exists for this address — invite still goes out
            const res = await vendorLinkService.sendInvite({ email, contactName: name, companyName: company, supplierId });
            if (res.ok && res.data?.success) {
                setStatus({ kind: 'ok', msg: `Invitation sent to ${email} (valid 7 days). Registration will auto-link the supplier record.` });
            }
            else {
                setStatus({ kind: 'err', msg: res.data?.error || res.error || 'Failed to send invitation' });
            }
        }
        catch (e) {
            setStatus({ kind: 'err', msg: e.message });
        }
        finally {
            setBusy(null);
        }
    };
    const openLinkPicker = async () => {
        setShowLinkPicker(true);
        if (users.length === 0) {
            const res = await vendorLinkService.listUsers();
            if (res.ok && res.data?.success)
                setUsers(res.data.users);
        }
    };
    const handleLink = async () => {
        if (!selectedUserId)
            return;
        setBusy('link');
        setStatus(null);
        try {
            const user = users.find(u => u.id === selectedUserId);
            const res = await vendorLinkService.createLink({
                ifsAccountId: getActiveIFSAccountId() || '',
                clientAddressId: String(summary.id),
                addressLabel: `${company || name} — ${address1}`,
                userId: selectedUserId,
                userEmail: user?.email,
            });
            if (res.ok && res.data?.success) {
                setStatus({ kind: 'ok', msg: `Linked — this address is now ${user?.email}'s default sender for this IFS account.` });
                setShowLinkPicker(false);
            }
            else {
                setStatus({ kind: 'err', msg: res.error || 'Failed to create link' });
            }
        }
        catch (e) {
            setStatus({ kind: 'err', msg: e.message });
        }
        finally {
            setBusy(null);
        }
    };
    return (_jsx("div", { onClick: onClose, style: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }, children: _jsxs("div", { onClick: e => e.stopPropagation(), style: { background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', width: '100%', maxWidth: 560, maxHeight: '85vh', overflowY: 'auto', padding: '1.5rem' }, children: [_jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }, children: [_jsxs("div", { children: [_jsx("h3", { style: { color: 'var(--text-primary)', margin: 0 }, children: name || 'Address Detail' }), _jsxs("p", { style: { color: 'var(--text-secondary)', margin: '0.25rem 0 0', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }, children: [kind === 'sender' ? 'Sender Address' : 'Recipient', " \u00B7 IFS ID ", summary.id] })] }), _jsx(ActionButton, { onClick: onClose, style: { padding: '0.4rem 0.6rem' }, children: _jsx(FaTimes, {}) })] }), loading ? (_jsx("p", { style: { color: 'var(--text-secondary)' }, children: "Loading full details\u2026" })) : (_jsxs("div", { style: { marginBottom: '1.25rem' }, children: [row('Company', company), row('Contact', name), row('Email', email), row('Phone', phone), row('Fax', f('fax', 'client_fax')), row('Address', [address1, address2].filter(Boolean).join(', ')), row('City / State', city && `${city}, ${state} ${zip}`), row('Country', country), row('Type', detail?.AddressType || (String(f('is_residential', 'is_residential')) === '1' ? 'Residential' : detail ? 'Business' : '')), kind === 'recipient' && detail && row('Address Verified', String(detail.client_is_address_verify) === '1' ? 'Yes' : 'No'), !detail && _jsx("p", { style: { color: 'var(--text-secondary)', fontSize: '0.8rem', marginTop: '0.5rem' }, children: "Full detail unavailable \u2014 showing list data." })] })), status && (_jsxs("p", { style: { padding: '0.6rem 0.8rem', fontSize: '0.85rem', border: '1px solid', borderColor: status.kind === 'ok' ? '#00c85355' : '#ff525255', color: status.kind === 'ok' ? '#00c853' : '#ff5252', background: 'var(--bg-tertiary)' }, children: [status.kind === 'ok' && _jsx(FaCheck, { style: { marginRight: 6 } }), status.msg] })), _jsxs("div", { style: { display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }, children: [_jsxs(ActionButton, { onClick: handleCreateSupplier, disabled: !!busy, children: [_jsx(FaUserPlus, {}), " ", busy === 'supplier' ? 'Creating…' : 'Create Supplier'] }), _jsxs(ActionButton, { onClick: handleInvite, disabled: !!busy || !email, title: !email ? 'No email on this IFS address' : undefined, children: [_jsx(FaEnvelope, {}), " ", busy === 'invite' ? 'Sending…' : 'Invite Vendor'] }), kind === 'sender' && (_jsxs(ActionButton, { onClick: openLinkPicker, disabled: !!busy, children: [_jsx(FaLink, {}), " Link to Account"] }))] }), showLinkPicker && (_jsxs("div", { style: { marginTop: '1rem', padding: '1rem', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }, children: [_jsx("p", { style: { color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '0 0 0.6rem' }, children: "This address becomes the selected user's default sender in the Create Shipment wizard (for the current IFS account)." }), _jsxs("div", { style: { display: 'flex', gap: '0.6rem' }, children: [_jsxs("select", { value: selectedUserId, onChange: e => setSelectedUserId(e.target.value), style: { flex: 1, padding: '0.5rem', background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }, children: [_jsx("option", { value: "", children: "Select a user\u2026" }), users.map(u => (_jsxs("option", { value: u.id, children: [[u.profile?.firstName, u.profile?.lastName].filter(Boolean).join(' ') || u.email, " (", u.email, ") \u2014 ", u.role] }, u.id)))] }), _jsx(ActionButton, { "$variant": "primary", onClick: handleLink, disabled: !selectedUserId || busy === 'link', children: busy === 'link' ? 'Linking…' : 'Link' })] })] }))] }) }));
};
export default AddressDetailPanel;
